import { Injectable, inject, signal, computed } from '@angular/core';
import type { StepParams } from '@enlighten/ts-shared/step-api';

import {
  API_BASE,
  FILE_MODULE_LIST,
  DUCKDB_IMPORT_FILES_ALLOWED,
  PROJECT,
  VERSION,
  RELOAD_GLOBAL_VAR,
  REMOVE_DUCKDB_FILE,
  RELOAD_FILES,
  PROJECT_FILE,
  SYNC_DUCKDB_FILES,
} from '../../constants/general.constants';

import {
  map,
  Observable,
  Subscription,
  from,
  catchError,
  throwError,
  firstValueFrom,
  switchMap,
  EMPTY,
  of,
  forkJoin,
} from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { FileModule, ProjectFileItem } from '../../interfaces/file.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import { MessageService } from '../message/message.service';
import { LocalforageService } from '../localforage/localforage.service';

import { ToastrService } from 'ngx-toastr';
import { Chunk } from '../../interfaces/step/step.interface';
import { Dialog } from '../../interfaces/create-dialog.interface';
import { DialogService } from '../dialog/dialog.service';
import { FileDataService } from '../file-data/file-data.service';
import {
  ERROR_SAVE_PROJECT_FILE,
} from '../../constants/additional-methods.constants';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';
import { AppStateService } from '../app-state/app-state.service';
import { CacheService } from '../cache/cache.service';
import { FSItem, NotebookFile, ProjectFile } from '@app/shared/interfaces';
import { ModuleListHelper } from '../../helpers/module-list.helper';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly localForageService = inject(LocalforageService);
  private readonly toastr = inject(ToastrService);
  private readonly customDialog = inject(DialogService);
  private readonly fileDataService = inject(FileDataService);
  private readonly appState = inject(AppStateService);
  private readonly cacheService = inject(CacheService);

  #appState = inject(AppStateService);
  public selectedId: number | null = null;

  // Signal-based module list for reactivity
  readonly #fileModuleList = signal<FileModule[]>([]);

  // Global signals (all projects - for internal use)
  moduleList = computed(() => this.#fileModuleList());
  moduleCount = computed(() => this.#fileModuleList().length);

  // Project-scoped signals (current project only - for UI)
  currentProjectModuleList = computed(() => {
    const currentProjectId = this.#appState.projectId();
    if (!currentProjectId) return []; // Safety: return empty if projectId not initialized
    return this.#fileModuleList().filter(m => m.projectId === currentProjectId);
  });
  currentProjectModuleCount = computed(() => {
    const currentProjectId = this.#appState.projectId();
    if (!currentProjectId) return 0; // Safety: return 0 if projectId not initialized
    return this.#fileModuleList().filter(m => m.projectId === currentProjectId).length;
  });

  // Legacy property for backward compatibility
  get fileModuleList(): FileModule[] {
    return this.#fileModuleList();
  }

  public fileList: ProjectFileItem[] = [];

  // ─────────────────────────────────────────────────────────────────────
  // Cache for file list to prevent duplicate API calls
  private fileListCache: Map<string, { data: ProjectFileItem[], timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 30000; // 30 seconds cache
  private pendingFileRequests: Map<string, Observable<ProjectFileItem[]>> = new Map();


  // ─────────────────────────────────────────────────────────────────────
  // Message subscription
  private messageSubscription!: Subscription;

  constructor() {
    // Don't immediately load module list - wait for project to be set
    // this.getFileModuleList();

    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe(async (message: any) => {
        if (message?.text === RELOAD_GLOBAL_VAR) {
          await this.clean();
          // Project modules are global - no version cleanup needed
          this.initializeModuleList().catch((error) => {
            console.error('Error initializing module list:', error);
          });
        }
      });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Create - UPDATED FOR API 2.5.0

  public createFile(
    data: any,
    parentId?: string | any,
    projectId: any = this.#appState.projectId(),
    versionId: any = this.#appState.versionId()
  ): Observable<object> {
    // Use new files endpoint for API 2.5.0
    return this.http.post<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`,
      data
    ).pipe(
      map((res) => {
        // Clear cache when file is created
        this.clearFileListCache();
        return res;
      })
    );
  }

  // Directory creation no longer supported in API 2.5.0
  public createDirectory(
    data: { name: string },
    parentId?: string | any,

    _versionId: any = this.#appState.versionId()
  ): Observable<object> {
    console.warn('Directory creation is no longer supported in API 2.5.0');
    return throwError(() => new Error('Directory creation is no longer supported in API 2.5.0'));
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Get Files Data - MIGRATED TO API 2.5.0
  // OPTIMIZED: Added caching to prevent duplicate API calls

  public get(bypassCache: boolean = false): Observable<ProjectFileItem[]> | any {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    if (!projectId || !versionId) {
      return;
    }

    const cacheKey = `${projectId}_${versionId}`;

    // Check cache first (unless bypass is requested)
    if (!bypassCache) {
      const cached = this.fileListCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        console.log('📦 FileService.get(): Returning cached file list');
        return of(cached.data);
      }

      // Check if there's already a pending request for this key
      const pendingRequest = this.pendingFileRequests.get(cacheKey);
      if (pendingRequest) {
        console.log('⏳ FileService.get(): Reusing pending request');
        return pendingRequest;
      }
    }

    console.log('🌐 FileService.get(): Making new API request');

    // Create new request
    const request$ = this.http
      .get<ProjectFile[]>(
        `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`
      )
      .pipe(
        map((res: ProjectFile[]) => {
          if (res === null || res === undefined) {
            return [];
          }
          // Convert ProjectFile[] to ProjectFileItem[] for UI
          const projectFileItems = this.convertToProjectFileItems(res);
          this.fileList = this.processFileList(projectFileItems);

          // Cache the result
          this.fileListCache.set(cacheKey, {
            data: projectFileItems,
            timestamp: Date.now()
          });

          // Remove from pending requests
          this.pendingFileRequests.delete(cacheKey);

          return projectFileItems;
        }),
        catchError((error) => {
          // Remove from pending requests on error
          this.pendingFileRequests.delete(cacheKey);
          return throwError(() => error);
        })
      );

    // Store as pending request
    this.pendingFileRequests.set(cacheKey, request$);

    return request$;
  }

  /**
   * Clear the file list cache
   * Call this when files are created, updated, or deleted
   */
  public clearFileListCache(): void {
    console.log('🧹 FileService: Clearing file list cache');
    this.fileListCache.clear();
  }

  public getFileFromUrl(url: string): Observable<any> {
    // Validate and clean the URL
    const validUrl = this.validateAndCleanUrl(url);

    // Create a simple header without the complex type issues
    const headers = new HttpHeaders({
      [GeneralHelpers.getInterceptorSkipHeader()]: ''  // Simplified approach assuming this is what getInterceptorSkipHeader does
    });
    return this.http.get(validUrl, { responseType: 'blob', headers });
  }

  public async getFileFromUrlAsBlob(url: string): Promise<Blob> {
    const validUrl = this.validateAndCleanUrl(url);
    try {
      const response = await firstValueFrom(
        this.http.post(`${API_BASE}/fetch-proxy-download`,
          { url: validUrl },
          { observe: 'response', responseType: 'blob' }
        )
      );

      // nc-server returns HTTP 200 when the proxy itself succeeded; the
      // upstream status is conveyed via X-Proxy-Status (mirrors
      // /fetch-proxy streaming-mode contract). 4xx/5xx upstream is not a
      // proxy failure — surface it as a download error with upstream context.
      const proxyStatus = Number.parseInt(response.headers.get('X-Proxy-Status') ?? '', 10);
      if (!Number.isFinite(proxyStatus) || proxyStatus < 200 || proxyStatus >= 300) {
        const text = response.headers.get('X-Proxy-Status-Text') ?? '';
        const code = Number.isFinite(proxyStatus) ? String(proxyStatus) : 'unknown';
        throw new Error(`Upstream returned HTTP ${code} ${text}`.trimEnd());
      }

      if (!response.body) {
        throw new Error('empty response body');
      }
      return response.body;
    } catch (error) {
      const msg = await this.formatHttpError(error);
      throw new Error(`Error downloading file from ${validUrl}: ${msg}`);
    }
  }

  private async formatHttpError(error: unknown): Promise<string> {
    if (error instanceof HttpErrorResponse) {
      // When responseType is 'blob', error.error is a Blob containing the
      // server's JSON error payload — read it as text to surface the real msg.
      let detail = error.message;
      if (error.error instanceof Blob) {
        try {
          const text = await error.error.text();
          detail = text || error.message;
        } catch {
          // fall through to default detail
        }
      } else if (typeof error.error === 'string') {
        detail = error.error;
      } else if (error.error && typeof error.error === 'object') {
        detail = JSON.stringify(error.error);
      }
      return `HTTP ${error.status} ${error.statusText} — ${detail}`;
    }
    if (error instanceof Error) return error.message;
    return JSON.stringify(error);
  }

  /**
   * Fetch any external URL via the authenticated nc-server proxy endpoint.
   * Designed for user scripts (ehq.fetch) that need to access arbitrary external APIs.
   * The backend enforces SSRF protection, HTTPS-only validation, rate limiting,
   * and audit logging — the front end only sanity-checks the URL shape so step
   * code remains portable between the headless (enoch) and browser runtimes.
   * Returns { status, statusText, headers, body }.
   */
  public async fetchProxy(params: StepParams['EHQ.fetch']): Promise<any> {
    // The parser has checked the URL shape, defaulted the method to GET and
    // turned an object body into JSON text (EHQ-10, D6).
    const { url: validUrl, method, headers, body } = params;

    // Enoch-style manual proxy: the definitive (headless) runtime proxies by
    // calling ehq.fetch("{apiUrl}fetch-proxy", { method: 'POST', body: JSON.stringify({ url, method, headers, body }) }).
    // The shared p21_sync lib uses this pattern. In the browser, naively wrapping
    // it would double-proxy (target the proxy endpoint, which then makes an
    // unauthenticated loopback call → 401). When the target IS the fetch-proxy
    // endpoint, the caller already handed us a fully-formed payload — forward it
    // directly (session auth applies; the redundant Bearer header is ignored). SCRUM-752
    //
    // Gate narrowly to avoid false positives: only a POST with a body, and only
    // when the URL parses and its path is the fetch-proxy endpoint. Anything else
    // (malformed URL, GET, no body) falls through to the browser-native path.
    let proxyEndpointTarget = false;
    try {
      proxyEndpointTarget = new URL(validUrl).pathname.endsWith('/fetch-proxy');
    } catch {
      // Malformed URL — let the browser-native path handle/report it.
    }
    if (proxyEndpointTarget && body !== undefined && method.toUpperCase() === 'POST') {
      let forwarded: unknown;
      try {
        forwarded = JSON.parse(body);
      } catch (e) {
        throw new Error(
          `ehq.fetch() to the fetch-proxy endpoint requires a JSON body — ${(e as Error).message}`
        );
      }
      // Enoch's EHQ.fetch returns the RAW outer HTTP response —
      // { status (outer HTTP), status_text, headers, body: <raw response text> } —
      // and the shared p21_sync lib then does json.loads(result["body"]) to parse
      // the nc-server fetch-proxy envelope. Returning responseType:'json' here
      // (direct.body) hands back the already-parsed envelope, unwrapping a level:
      // result["body"] would be the upstream body, not the envelope string, so
      // json.loads parses the wrong thing (browser diagnose died on P21 token auth
      // with "Expecting value: line 1 column 1"). Match enoch: fetch as text and
      // wrap. SCRUM-753 (follow-up to SCRUM-752).
      const direct = await firstValueFrom(
        this.http.post(`${API_BASE}/fetch-proxy`, forwarded, { observe: 'response', responseType: 'text' })
      );
      const responseHeaders: Record<string, string> = {};
      for (const key of direct.headers.keys()) {
        responseHeaders[key] = direct.headers.get(key) ?? '';
      }
      return {
        status: direct.status,
        status_text: direct.statusText,
        headers: responseHeaders,
        body: direct.body ?? '',
      };
    }

    const payload: { url: string; method: string; headers?: Record<string, string>; body?: string } = {
      url: validUrl,
      method,
    };
    if (headers) payload.headers = headers;
    if (body) payload.body = body;

    const response = await firstValueFrom(
      this.http.post(`${API_BASE}/fetch-proxy`, payload, { observe: 'response', responseType: 'json' })
    );

    return response.body;
  }

  /**
   * Validates and cleans URL parameter to ensure it's a proper string
   */
  private validateAndCleanUrl(url: string): string {
    if (!url || !url.trim()) {
      throw new Error('Invalid or empty URL provided');
    }

    const cleanUrl = url.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      throw new Error('URL must start with http:// or https://');
    }

    return cleanUrl;
  }

  /**
   * `EHQ.saveFileFromUrl`, with enoch's contract (EHQ-10, D7): downloads the
   * URL through the server proxy and saves it as one of the step's own files,
   * in the cache like `EHQ.saveFile` (enoch writes the job's workflow files),
   * replacing a file of that name. Returns the name. The parser has validated
   * the URL and derived or checked the name.
   */
  public async saveFileFromUrl(
    params: StepParams['EHQ.saveFileFromUrl'],
    chunkContext: ExecutionContext,
    language?: string,
  ): Promise<string> {
    const { url, fileName } = params;
    try {
      // Download first, so a failed download never touches the existing file.
      const blob = await this.getFileFromUrlAsBlob(url);
      const content = new Uint8Array(await blob.arrayBuffer());
      await this.cacheService.saveNotebookFile({ fileName, content }, chunkContext, language);
      return fileName;
    } catch (error) {
      this.toastr.error(`Failed to save file from URL: ${error}`);
      throw new Error(`Failed to save file from URL ${url}: ${error}`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Get file content by id - MIGRATED TO API 2.5.0

  /**
   * Gets a file by ID with error handling and caching
   * Validates that the file ID exists before making the request to avoid 404 errors
   */
  public getById(
    fileId: string | any,
    responseType: 'text' | 'blob' | 'arraybuffer' | 'document' | 'json' = 'text'
  ): Observable<any> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();

    if (!projectId || !versionId) {
      console.warn('No projectId or versionId available for getById');
      return EMPTY;
    }

    // Convert fileId to number for validation
    const fileIdNum = typeof fileId === 'string' ? parseInt(fileId, 10) : fileId;
    if (isNaN(fileIdNum)) {
      console.warn(`Invalid fileId: ${fileId}`);
      return EMPTY;
    }

    const url = `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}/content`;

    // First validate that the file ID exists, then get from the HTTP endpoint
    return from(this.validateFileIdExists(projectId, versionId, fileIdNum)).pipe(
      switchMap((exists) => {
        if (!exists) {
          console.warn(`File ID ${fileId} does not exist in project ${projectId}, version ${versionId}. Skipping request.`);
          return EMPTY;
        }
        return this.http.get(url, { responseType } as any).pipe(
          catchError(error => this.handleGetByIdError(error, fileId, responseType, url))
        );
      })
    );
  }

  /**
   * Handle error when getting file by ID
   */
  private handleGetByIdError(
    error: any,
    fileId: string,
    responseType: 'text' | 'blob' | 'arraybuffer' | 'document' | 'json',
    url: string
  ): Observable<any> {
    console.warn('Error fetching file, trying from cache:', error);

    // First try to get from the project cache - updated to remove unused parameter
    return this.getCachedFile(fileId).pipe(
      catchError(cacheError => this.handleCacheError(cacheError, url, responseType))
    );
  }

  /**
   * Get a file from cache
   */
  private getCachedFile(
    fileId: string,
    // responseType parameter is not used, so remove it
  ): Observable<any> {
    return from(this.fileDataService.getFileFromIndexDB(fileId, PROJECT_FILE as any));
  }

  /**
   * Handle error when getting from cache
   */
  private handleCacheError(
    cacheError: any,
    url: string,
    responseType: 'text' | 'blob' | 'arraybuffer' | 'document' | 'json'
  ): Observable<any> {
    console.warn('Error fetching from cache:', cacheError);

    // If cache fails too, try from CDN as a last resort
    return this.http.get(url, { responseType } as any).pipe(
      catchError(finalError => {
        console.error('All file retrieval methods failed:', finalError);
        return throwError(() => finalError);
      })
    );
  }

  public getFileItemById(): ProjectFileItem | any {
    if (this.fileList?.length > 0) {
      if (this.selectedId === null) {
        this.selectedId = this.fileList[0].fileId;
        return this.fileList[0];
      }

      for (const element of this.fileList) {
        if (element.fileId === this.selectedId) {
          return element;
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Update - UPDATED FOR API 2.5.0

  public update(data: any, fileId: string): Observable<object> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    return this.http.put<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`,
      data
    ).pipe(
      map((res) => {
        // Clear cache when file is updated
        this.clearFileListCache();
        return res;
      })
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Delete file - UPDATED FOR API 2.5.0

  public delete(fileId: string | any): Observable<object> {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    return this.http
      .delete<ProjectFile>(
        `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`
      )
      .pipe(
        map(async (res) => {
          await this.fileDataService.deleteFileFromIndexDB(
            fileId,
            PROJECT_FILE
          );
          // Clear cache when file is deleted
          this.clearFileListCache();
          return res;
        })
      );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // File Module List - UPDATED FOR PROJECTFILEITEM

  /**
   * Apply modules from storage to signal (for load operations - no persist)
   */
  private applyModulesFromStorage(modules: FileModule[]): void {
    this.#fileModuleList.set(modules);
  }

  /**
   * Save modules to storage (for mutate operations)
   */
  private async saveModulesToStorage(modules: FileModule[]): Promise<void> {
    this.#fileModuleList.set(modules);
    await this.localForageService.setItem(
      `${FILE_MODULE_LIST}_${this.#appState.projectId()}`,
      GeneralHelpers.jsonStringify(modules)
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Get File Module List

  public getFileModuleList(extensions?: string[]): FileModule[] | any {
    // CRITICAL: Capture projectId at method start to detect race conditions
    const operationProjectId = this.#appState.projectId();
    let result: any = [];

    return this.localForageService
      .getItem(`${FILE_MODULE_LIST}_${operationProjectId}`)
      .then((fileModuleList: any) => {
        // CRITICAL: Abort if projectId changed during async operation
        if (this.#appState.projectId() !== operationProjectId) {
          console.warn(`⚠️ FileService: Project changed during getFileModuleList, returning empty`);
          return [];
        }

        if (fileModuleList !== null) {
          const parsed = GeneralHelpers.jsonParse(fileModuleList);

          // Validate that parsed data is an array
          if (Array.isArray(parsed)) {
            const currentProjectId = operationProjectId;

            // CRITICAL: Filter by projectId to prevent cross-project contamination
            // Modules are project-global (all versions), but isolated per project
            result = parsed.filter((item: FileModule) =>
              item.projectId === currentProjectId
            );

            console.log(`📋 FileService: Loaded ${result.length} modules for project ${currentProjectId} (filtered from ${parsed.length} total)`);
          } else {
            // Log error and reset to empty array if data is corrupted
            console.error('FileModuleList data is not an array, resetting:', parsed);
            // Reset to empty array for corrupted data
            this.applyModulesFromStorage([]);
            result = [];
          }
        } else {
          result = this.fileModuleList;
        }
        return extensions?.length
          ? this.getFilteredModuleList(result, extensions)
          : result;
      });
  }

  private getFilteredModuleList(
    fileList: FileModule[],
    extensions: string[]
  ): FileModule[] {
    const filteredModules: FileModule[] = [];
    if (fileList?.length === 0 || extensions?.length === 0) {
      return filteredModules;
    }

    for (const element of fileList) {
      if (extensions.includes(element?.extension)) {
        filteredModules.push(element);
      }
    }

    return filteredModules;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Local Storage Modules;

  // ─────────────────────────────────────────────────────────────────────
  // Delete

  public async deleteInModuleList(fileId: number): Promise<void> {
    const list = await this.getFileModuleList();
    const index = list.findIndex((item: FileModule) => item.fileId === fileId);
    if (index > -1) {
      const removed = list[index];
      list.splice(index, 1);

      console.log(`Module "${removed.name}" removed from list`);

      if (DUCKDB_IMPORT_FILES_ALLOWED.includes(removed.extension as any)) {
        this.messageService.sendMessage(REMOVE_DUCKDB_FILE, removed.name);
      }

      if (removed.name !== undefined) {
        this.removeLocalforageItem(removed.name);
      }

      await this.saveModulesToStorage(list);
    } else {
      console.warn(`Module with fileId "${fileId}" not found for deletion`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Add to file module list - UPDATED FOR PROJECTFILEITEM
  // Add or Update

  public async addToModuleList(file: ProjectFile, data?: ArrayBuffer | string): Promise<void> {
    // CRITICAL: Capture projectId at method start to prevent race condition
    const operationProjectId = this.#appState.projectId();

    console.log(`💾 FileService: Adding module ${file.name} (fileId: ${file.fileId}) for project ${operationProjectId}`);

    const list = await this.getFileModuleList();

    // CRITICAL: Abort if projectId changed during async operation
    if (this.#appState.projectId() !== operationProjectId) {
      console.warn(`⚠️ FileService: Project changed during addToModuleList (${operationProjectId} → ${this.#appState.projectId()}), aborting operation`);
      return;
    }

    const projectId = operationProjectId;

    // Simple storage key without workarounds
    const storageKey = `${projectId}_${file.fileId}_${file.name}`;
    console.log(`🔑 FileService: Storage key: ${storageKey}`);

    // Additional safety check - ensure list is an array
    let safeList: FileModule[];
    if (!Array.isArray(list)) {
      console.error('📚❌ FileService: Expected array but got:', typeof list, list);
      // Skip corrupted data
      safeList = []; // Use empty array as fallback
    } else {
      safeList = list;
      console.log(`📚 FileService: Current module list has ${safeList.length} items`);
    }

    // Use composite key (projectId + fileId) for uniqueness
    // Modules are project-global, versionId is metadata only
    const index = safeList.findIndex(m =>
      m.projectId === operationProjectId &&
      m.fileId === file.fileId
    );
    const extension = GeneralHelpers.fileExtensionFromString(file.name);

    console.log(`🏷️ FileService: Module extension detected as "${extension}"`);

    try {
      // CRITICAL FIX: Use await for proper sequencing
      await this.localForageService.setItem(storageKey, data);
      console.log(`✅ FileService: Module data stored successfully in LocalForage for ${file.name}`);

      // Clean API v0.3.0 FileModule
      const fileModuleItem: FileModule = {
        name: file.name,
        fileId: file.fileId,
        projectId: operationProjectId,
        versionId: file.versionId,
        extension: extension,
        isGlobal: true
      };

      if (index > -1) {
        console.log(`🔄 FileService: Updating existing module at index ${index} for ${file.name}`);
        safeList[index] = fileModuleItem;
      } else {
        console.log(`➕ FileService: Adding new module to list for ${file.name}`);
        safeList.push(fileModuleItem);
      }

      if (DUCKDB_IMPORT_FILES_ALLOWED.includes(fileModuleItem.extension as any)) {
        console.log(`🗄️ FileService: Triggering DuckDB sync for ${file.name} (${extension})`);
        this.messageService.sendMessage(SYNC_DUCKDB_FILES);
      }

      console.log(`📋 FileService: Saving updated module list with ${safeList.length} items`);

      // CRITICAL: Final check before saving - abort if project changed
      if (this.#appState.projectId() !== operationProjectId) {
        console.warn(`⚠️ FileService: Project changed before save (${operationProjectId} → ${this.#appState.projectId()}), aborting to prevent contamination`);
        return;
      }

      await this.saveModulesToStorage(safeList);
      console.log(`🎉 FileService: Module ${file.name} successfully added to module list!`);

      // Data successfully saved

    } catch (error) {
      console.error(`❌ FileService: Failed to save module to LocalForage:`, {
        key: storageKey,
        fileName: file.name,
        projectId: projectId,
        error: (error as any)?.message || error
      });
      // Propagate error to FilesHelper for user notification
      throw error;
    }
  }

  public isInModuleList(node: ProjectFileItem): boolean {
    const list = this.fileModuleList;
    const index = GeneralHelpers.findIndexInArrayByProp(
      list as unknown as Record<string, unknown>[],
      'fileId',
      node.fileId
    );
    return index > -1;
  }

  /**
   * Auto-add file to module list if it's a Python or JavaScript file
   * Delegates to ModuleListHelper for centralized logic
   */
  public async autoAddToModuleListIfEligible(file: ProjectFile | NotebookFile, data?: ArrayBuffer | string): Promise<void> {
    // Delegate to centralized ModuleListHelper
    await ModuleListHelper.autoAddFileToModulesIfEligible(file, this, data);
  }

  public async getFileModuleByName(name: string) {
    const list = await this.getFileModuleList();
    const projectId = this.#appState.projectId();
    const fileModule = GeneralHelpers.findElementInArrayByProp(
      list,
      'name',
      `${projectId}_${name}`
    ) as FileModule | undefined;
    return fileModule;
  }

  private getFileModuleIndex(id: string): number {
    const index = this.fileModuleList.findIndex(
      (item: FileModule) =>
        item.fileId?.toString() === id
    );
    return index; // Return actual index (-1 if not found)
  }

  private getFileModuleIndexbyName(name: string): number {
    const index = this.fileModuleList.findIndex(
      (item: FileModule) => item.name === name
    );
    return index; // Return actual index (-1 if not found)
  }

  public async clean() {
    console.log('🧹 FileService: Clearing module list');

    // Use consistent API for signal update
    await this.saveModulesToStorage([]);

    // Delete the key entirely for proper "reset to initial state" semantics
    const projectId = this.#appState.projectId();
    if (projectId) {
      await this.localForageService.removeItem(`${FILE_MODULE_LIST}_${projectId}`);
      console.log(`🧹 FileService: Module list cleared for project ${projectId}`);
    }

    // Clear file list cache for consistency
    this.clearFileListCache();
  }

  public async updateModuleGlobal(name: string, isGlobal: boolean) {
    const index = this.getFileModuleIndexbyName(name);
    if (index > -1) {  // Check that module was found
      const currentList = this.#fileModuleList();
      currentList[index].isGlobal = isGlobal;
      await this.saveModulesToStorage(currentList);
    } else {
      console.warn(`Module with name "${name}" not found for global update`);
    }
  }

  private processFileList(data: ProjectFileItem[]): ProjectFileItem[] {
    for (const item of data) {
      item.isInModuleList = this.isInModuleList(item);
    }
    return data;
  }


  public async getParentId() {
    // In API 2.5.0, files are flat - no parent/child relationships
    // Return null as parentId is no longer needed
    return null;
  }

  private async populateFileList(): Promise<void> {
    try {
      const files = await this.get().toPromise();
      this.fileList = files || [];
    } catch (error) {
      console.error('Error fetching file list', error);
    }
  }


  public findFileItemParent(_id: any = this.selectedId): number | null {
    // In API 2.5.0, there are no parent/child relationships
    // Return null as files are flat
    return null;
  }

  public async saveToFile(
    fileContent: any,
    chunkData: Chunk,
    fileExtension: string,
    fileName: string
  ) {
    const parentId = await this.getParentId();
    const formData = this.fileDataService.prepareFileData(
      fileName,
      fileExtension,
      fileContent,
      chunkData
    );
    const fullFileName = fileExtension ? `${fileName}.${fileExtension}` : fileName;
    this.createFile(formData, parentId).subscribe({
      next: () => {
        this.toastr.success(`Item ${fullFileName} saved to File panel`);
        this.messageService.sendMessage(RELOAD_FILES);
      },
      error: () => { },
    });
  }

  public getLocalforageItem(key: string) {
    return this.localForageService.getItem(key);
  }

  public setLocalforageItem(key: string, value: any) {
    return this.localForageService.setItem(key, value);
  }

  public removeLocalforageItem(key: string) {
    return this.localForageService.removeItem(key);
  }

  // ─────────────────────────────────────────────────────────────────────
  // This method is used in quick.js and pyodide
  // ─────────────────────────────────────────────────────────────────────
  public async saveFile(data: any[], chunkContext: ExecutionContext) {
    this.validateSaveFileInput(data, chunkContext);

    const fileNameWithExtension = data[0];
    const fileContent = data[1];
    const { fileName, fileExtension, reconstructedFileName } = this.parseFileName(fileNameWithExtension);

    await this.ensureFileListLoaded();

    const existingFile = await this.findExistingFile(reconstructedFileName, fileNameWithExtension);

    if (existingFile) {
      await this.handleFileOverwrite(existingFile, fileName, fileExtension, fileContent, reconstructedFileName, fileNameWithExtension, chunkContext);
    } else {
      await this.createNewFile(fileName, fileExtension, fileContent, reconstructedFileName, chunkContext);
    }
  }

  private validateSaveFileInput(data: any[], chunkContext: ExecutionContext): void {
    if (!data || data.length < 2 || typeof data[0] !== 'string' || !data[1]) {
      const error = new Error(ERROR_SAVE_PROJECT_FILE);
      chunkContext.addMessage(ERROR_SAVE_PROJECT_FILE, 'danger');
      throw error;
    }
  }

  private parseFileName(fileNameWithExtension: string): { fileName: string; fileExtension: string; reconstructedFileName: string } {
    const fileName = GeneralHelpers.fileNameFromString(fileNameWithExtension);
    const fileExtension = GeneralHelpers.fileExtensionFromString(fileNameWithExtension);
    const reconstructedFileName = fileExtension ? `${fileName}.${fileExtension}` : fileName;
    return { fileName, fileExtension, reconstructedFileName };
  }

  private async ensureFileListLoaded(): Promise<void> {
    if (Array.isArray(this.fileList) && this.fileList.length === 0) {
      this.fileList = await this.get().toPromise();
    }
  }

  private async findExistingFile(reconstructedFileName: string, fileNameWithExtension: string): Promise<ProjectFileItem | undefined> {
    const presentedFileIndex = GeneralHelpers.findIndexInArrayByProp(
      this.fileList as unknown as Record<string, unknown>[],
      'name',
      reconstructedFileName
    );

    if (presentedFileIndex > -1) {
      return this.fileList.find(
        (item: ProjectFileItem) => item.name === fileNameWithExtension
      );
    }
    return undefined;
  }

  private async handleFileOverwrite(
    projectFileItem: ProjectFileItem,
    fileName: string,
    fileExtension: string,
    fileContent: any,
    reconstructedFileName: string,
    fileNameWithExtension: string,
    chunkContext: ExecutionContext
  ): Promise<void> {
    try {
      const confirmed = await firstValueFrom(this.openFileOverwriteDialog(fileNameWithExtension));

      if (confirmed) {
        await this.overwriteExistingFile(projectFileItem, fileName, fileExtension, fileContent, reconstructedFileName, chunkContext);
      } else {
        this.notifyFileSaveCancelled(fileNameWithExtension, chunkContext);
      }
    } catch (_error) {
      this.notifyFileSaveCancelled(fileNameWithExtension, chunkContext);
    }
  }

  private async overwriteExistingFile(
    projectFileItem: ProjectFileItem,
    fileName: string,
    fileExtension: string,
    fileContent: any,
    reconstructedFileName: string,
    chunkContext: ExecutionContext
  ): Promise<void> {
    const parentId = await this.getParentId();
    const formData = this.fileDataService.prepareFileData(fileName, fileExtension, fileContent);

    return new Promise((resolve, reject) => {
      this.delete(projectFileItem.fileId.toString()).subscribe({
        next: () => {
          this.createFile(formData, parentId).subscribe({
            next: () => {
              this.toastr.success(`File ${reconstructedFileName} overwritten successfully`);
              this.messageService.sendMessage(RELOAD_FILES);
              resolve();
            },
            error: (error) => {
              const errorMessage = `Failed to create file after deletion: ${error?.message || 'Unknown error'}`;
              chunkContext.addMessage(errorMessage, 'danger');
              this.toastr.error(errorMessage);
              reject(error);
            }
          });
        },
        error: (error) => {
          const errorMessage = `Failed to delete existing file: ${error?.message || 'Unknown error'}`;
          chunkContext.addMessage(errorMessage, 'danger');
          this.toastr.error(errorMessage);
          reject(error);
        }
      });
    });
  }

  private notifyFileSaveCancelled(fileNameWithExtension: string, chunkContext: ExecutionContext): void {
    const cancelMessage = `File save cancelled: ${fileNameWithExtension} already exists`;
    chunkContext.addMessage(cancelMessage, 'warning');
    this.toastr.warning(cancelMessage);
  }

  private async createNewFile(
    fileName: string,
    fileExtension: string,
    fileContent: any,
    reconstructedFileName: string,
    chunkContext: ExecutionContext
  ): Promise<void> {
    try {
      const parentId = await this.getParentId();
      const formData = this.fileDataService.prepareFileData(fileName, fileExtension, fileContent);

      return new Promise((resolve, reject) => {
        this.createFile(formData, parentId).subscribe({
          next: () => {
            this.toastr.success(`File ${reconstructedFileName} saved to File panel`);
            this.messageService.sendMessage(RELOAD_FILES);
            resolve();
          },
          error: (error) => {
            const errorMessage = `Failed to create file: ${error?.message || 'Unknown error'}`;
            chunkContext.addMessage(errorMessage, 'danger');
            this.toastr.error(errorMessage);
            reject(error);
          }
        });
      });
    } catch (error) {
      const errorMessage = `Error preparing file data: ${error}`;
      chunkContext.addMessage(errorMessage, 'danger');
      this.toastr.error(errorMessage);
      throw error;
    }
  }

  public async initializeModuleList(): Promise<void> {
    try {
      // CRITICAL: Capture projectId FIRST to prevent race condition
      const projectId = this.#appState.projectId();
      const version = this.#appState.version();

      console.log(`🔄 FileService: Initializing module list for project ${projectId}`);

      // Load from LocalForage FIRST
      const fileModuleList = await this.getFileModuleList();

      // Update signal IMMEDIATELY with loaded data (no clear!)
      // This ensures shouldAddToModules() sees current state during sync
      // skipPersist: true to avoid saving data we just loaded
      this.applyModulesFromStorage(fileModuleList || []);

      console.log(`📚 FileService: Loaded ${fileModuleList?.length || 0} modules from LocalForage`);

      // ALWAYS sync to ensure new files from Project Files are added
      if (projectId && version?.versionId) {
        console.log('📋 FileService: Syncing from project files');
        await ModuleListHelper.syncProjectFilesToModules(projectId, version.versionId, this);
      }

      console.log(`📚 FileService: Initialized module list with ${this.moduleCount()} modules for project ${this.#appState.projectId()}`);
    } catch (error: unknown) {
      console.error('📚❌ FileService: Error initializing module list:', error);
      this.applyModulesFromStorage([]);
    }
  }

  /**
   * Get raw module list without version filtering (internal use only)
   * Used by cleanup methods to access all modules across all versions
   */
  async getFileModuleListRaw(): Promise<FileModule[]> {
    const data = await this.localForageService.getItem(`${FILE_MODULE_LIST}_${this.#appState.projectId()}`);
    if (data) {
      const parsed = GeneralHelpers.jsonParse(data as string);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  }

  // Legacy methods removed - using only API v0.3.0
  public async getFile(args: string[]): Promise<any> {
    if (!args.length || !args[0]) {
      throw new Error('Invalid or missing file name');
    }

    const fileName = args[0];
    const fileExtension = GeneralHelpers.fileExtensionFromString(fileName);
    const responseType = args[1] || this.fileDataService.getResponseType(fileExtension);

    const projectFiles = await this.get().toPromise();
    const projectFile = GeneralHelpers.findElementInArrayByProp(
      projectFiles,
      'name',
      fileName
    );

    if (projectFile) {
      const fileId = projectFile['fileId'];
      return await firstValueFrom(this.getById(fileId, responseType as any));
    }

    throw new Error(`File '${fileName}' not found`);
  }

  public openWatchFileDialog(node: ProjectFileItem): Observable<any> {
    const dialogData: Dialog = {
      title: `Watch file ${node.name}`,
      label: 'Do you want to watch this file for changes?',
      confirmCaption: 'Watch',
      cancelCaption: 'Cancel',
      hasClose: true
    };

    return this.customDialog.confirmDialog(dialogData);
  }

  public openFileOverwriteDialog(fileName: string): Observable<any> {
    const dialogData: Dialog = {
      title: `File already exists`,
      label: `File "${fileName}" already exists. Do you want to overwrite it?`,
      confirmCaption: 'Overwrite',
      cancelCaption: 'Cancel',
      hasClose: true
    };

    return this.customDialog.confirmDialog(dialogData);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Legacy API methods (deprecated) - Use ProjectFile methods instead
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Get files for a specific project version (DEPRECATED - API 2.4.6)
   * @deprecated Use getProjectFiles instead
   */
  getFiles(projectId: number, versionId: number): Observable<FSItem[]> {
    console.warn('getFiles is deprecated. Use getProjectFiles instead.');
    return this.http.get<FSItem[]>(`${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`);
  }

  /**
   * Get content of a specific file (DEPRECATED - API 2.4.6)
   * @deprecated Use getProjectFileContent instead
   */
  getFileContent(projectId: number, versionId: number, fileId: number): Observable<Blob> {
    console.warn('getFileContent is deprecated. Use getProjectFileContent instead.');
    return this.http.get(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}/content`,
      { responseType: 'blob' }
    );
  }

  /**
   * Update content of a file (DEPRECATED - API 2.4.6)
   * @deprecated Use updateProjectFileContent instead
   */
  updateFileContent(
    projectId: number,
    versionId: number,
    fileId: number,
    content: Blob
  ): Observable<ProjectFile> {
    console.warn('updateFileContent is deprecated. Use updateProjectFileContent instead.');
    return this.http.put<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`,
      content
    );
  }

  /**
   * Create a new file (DEPRECATED - API 2.4.6)
   * @deprecated Use createProjectFile instead
   */
  createFileApi(projectId: number, versionId: number): Observable<ProjectFile> {
    console.warn('createFileApi is deprecated. Use createProjectFile instead.');
    return this.http.post<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`,
      null
    );
  }

  /**
   * Delete a file (DEPRECATED - API 2.4.6)
   * @deprecated Use deleteProjectFile instead
   */
  deleteFile(projectId: number, versionId: number, fileId: number): Observable<void> {
    console.warn('deleteFile is deprecated. Use deleteProjectFile instead.');
    return this.http.delete<void>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`
    );
  }

  /**
   * Create a new directory (DEPRECATED - API 2.4.6)
   * @deprecated Directory creation no longer supported in API 2.5.0
   */
  createDirectoryApi(

    _projectId: number,

    _versionId: number,

    _parentId: number,

    _name: string
  ): Observable<ProjectFile> {
    console.warn('Directory creation is no longer supported in API 2.5.0');
    return throwError(() => new Error('Directory creation is no longer supported in API 2.5.0'));
  }

  // ─────────────────────────────────────────────────────────────────────
  // New ProjectFile API methods (API 2.5.0)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Get all files for a project version (API 2.5.0)
   * OPTIMIZED: Uses the same caching mechanism as get() method
   */
  getProjectFiles(projectId: number, versionId: number, bypassCache: boolean = false): Observable<ProjectFile[]> {
    const cacheKey = `${projectId}_${versionId}`;

    // Check cache first (unless bypass is requested)
    if (!bypassCache) {
      const cached = this.fileListCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        console.log('📦 FileService.getProjectFiles(): Returning cached file list');
        // Return ProjectFile[] instead of ProjectFileItem[]
        return of(cached.data.map(item => this.convertProjectFileItemToProjectFile(item)));
      }

      // Check if there's already a pending request for this key
      const pendingRequest = this.pendingFileRequests.get(cacheKey);
      if (pendingRequest) {
        console.log('⏳ FileService.getProjectFiles(): Reusing pending request');
        // Convert ProjectFileItem[] to ProjectFile[]
        return pendingRequest.pipe(
          map(items => items.map(item => this.convertProjectFileItemToProjectFile(item)))
        );
      }
    }

    console.log('🌐 FileService.getProjectFiles(): Making new API request');

    // Create new request
    const request$ = this.http.get<ProjectFile[]>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`
    ).pipe(
      map((res: ProjectFile[]) => {
        if (res === null || res === undefined) {
          return [];
        }

        // Convert to ProjectFileItem[] for caching
        const projectFileItems = this.convertToProjectFileItems(res);

        // Cache the result
        this.fileListCache.set(cacheKey, {
          data: projectFileItems,
          timestamp: Date.now()
        });

        return res;
      }),
      catchError((error) => {
        console.error('Error fetching project files:', error);
        return throwError(() => error);
      })
    );

    return request$;
  }

  /**
   * Helper method to convert ProjectFileItem back to ProjectFile
   */
  private convertProjectFileItemToProjectFile(item: ProjectFileItem): ProjectFile {
    return {
      fileId: item.fileId,
      versionId: item.versionId,
      name: item.name,
      etag: item.etag || undefined,
      filesize: item.filesize,
      modifiedAt: item.modifiedAt
    };
  }

  /**
   * Check if a file ID exists in the current project files
   * This helps avoid 404 errors by validating file IDs before fetching content
   */
  private async validateFileIdExists(projectId: number, versionId: number, fileId: number): Promise<boolean> {
    try {
      const files = await firstValueFrom(this.getProjectFiles(projectId, versionId));
      return files.some(file => file.fileId === fileId);
    } catch (error) {
      console.warn(`Error validating file ID ${fileId}:`, error);
      // If validation fails, return true to allow the request (fail gracefully)
      return true;
    }
  }

  /**
   * Get file content directly (API 2.5.0)
   * Validates that the file ID exists before making the request to avoid 404 errors
   */
  getProjectFileContent(projectId: number, versionId: number, fileId: number): Observable<Blob> {
    return this.getProjectFileContentForVersion(
      projectId,
      versionId,
      fileId,
      'blob'
    ) as Observable<Blob>;
  }

  /**
   * Get file content for a specific project version with response type
   */
  getProjectFileContentForVersion(
    projectId: number,
    versionId: number,
    fileId: number,
    responseType: 'text' | 'blob' | 'arraybuffer' | 'document' | 'json' = 'text'
  ): Observable<any> {
    // First validate that the file ID exists
    return from(this.validateFileIdExists(projectId, versionId, fileId)).pipe(
      switchMap((exists) => {
        if (!exists) {
          console.warn(`File ID ${fileId} does not exist in project ${projectId}, version ${versionId}. Skipping request.`);
          return EMPTY;
        }
        return this.http.get(
          `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}/content`,
          { responseType } as any
        );
      }),
      catchError((error) => {
        // If it's a 404, log it but don't throw to avoid noise
        if (error.status === 404) {
          console.warn(`File ID ${fileId} not found (404). It may have been deleted or never existed.`);
          return EMPTY;
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Update file content (API 2.5.0)
   */
  updateProjectFileContent(
    projectId: number,
    versionId: number,
    fileId: number,
    content: Blob
  ): Observable<ProjectFile> {
    const formData = new FormData();
    formData.append('file', content);

    return this.http.put<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`,
      formData
    ).pipe(
      map((res) => {
        // Clear cache when file is updated
        this.clearFileListCache();
        return res;
      })
    );
  }

  /**
   * Create a new file (API 2.5.0)
   */
  createProjectFile(projectId: number, versionId: number, file: File): Observable<ProjectFile> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ProjectFile>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files`,
      formData
    ).pipe(
      map((res) => {
        // Clear cache when file is created
        this.clearFileListCache();
        return res;
      })
    );
  }

  /**
   * Create a file with content (API 2.5.0)
   */
  createProjectFileWithContent(
    projectId: number,
    versionId: number,
    fileName: string,
    content: string | Blob,
    mimeType: string = 'text/plain'
  ): Observable<ProjectFile> {
    const file = new File([content], fileName, { type: mimeType });

    return this.createProjectFile(projectId, versionId, file);
  }

  /**
   * Delete a file (API 2.5.0)
   */
  deleteProjectFile(projectId: number, versionId: number, fileId: number): Observable<void> {
    return this.http.delete<void>(
      `${API_BASE}/${PROJECT}/${projectId}/${VERSION}/${versionId}/files/${fileId}`
    ).pipe(
      map((res) => {
        // Clear cache when file is deleted
        this.clearFileListCache();
        return res;
      })
    );
  }

  /**
   * Delete multiple files (API 2.5.0)
   * @param projectId Project ID
   * @param versionId Version ID
   * @param fileIds Array of file IDs to delete
   * @returns Observable that emits an array of results for each deletion
   */
  deleteProjectFiles(
    projectId: number,
    versionId: number,
    fileIds: number[]
  ): Observable<{ fileId: number; success: boolean; error?: any }[]> {
    // Create delete observables for each file
    const deleteObservables = fileIds.map(fileId =>
      this.deleteProjectFile(projectId, versionId, fileId).pipe(
        map(() => ({ fileId, success: true })),
        catchError(error => {
          console.error(`Error deleting file ${fileId}:`, error);
          return of({ fileId, success: false, error });
        })
      )
    );

    // Execute all deletions in parallel
    return forkJoin(deleteObservables);
  }

  /**
   * Convert ProjectFile to ProjectFileItem for UI use
   */
  convertToProjectFileItem(projectFile: ProjectFile): ProjectFileItem {
    return {
      fileId: projectFile.fileId,
      versionId: projectFile.versionId,
      name: projectFile.name,
      etag: projectFile.etag || '',
      filesize: projectFile.filesize,
      modifiedAt: projectFile.modifiedAt,
      fileType: GeneralHelpers.fileTypeFromProjectFile({
        name: projectFile.name,
        etag: projectFile.etag || undefined
      })
    };
  }

  /**
   * Convert array of ProjectFiles to ProjectFileItems
   */
  convertToProjectFileItems(projectFiles: ProjectFile[]): ProjectFileItem[] {
    return projectFiles.map(file => this.convertToProjectFileItem(file));
  }

  /**
   * List all project files - returns array of file names
   * This method is exposed to Python/JavaScript via ehq.listProjectFiles()
   */
  public async listProjectFiles(): Promise<string[]> {
    try {
      const projectId = this.#appState.projectId();
      const versionId = this.#appState.versionId();

      if (!projectId || !versionId) {
        console.warn('No projectId or versionId available for listing project files');
        return [];
      }

      const files = await firstValueFrom(this.getProjectFiles(projectId, versionId));
      return files.map(file => file.name);
    } catch (error) {
      console.error('Error listing project files:', error);
      return [];
    }
  }
}
